/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Play } from 'lucide-react';
import { useState } from 'react';

/* Import API functions */
import { EeCheckApiError, eeCheckApi } from '@/api/eeCheckApi';

/* Import components */
import StatusModal from '@/components/feedback/StatusModal';
import StaticStoredPlotlyPlot from '@/components/charts/StaticStoredPlotlyPlot';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';
import { useSession } from '@/context/SessionContext';

/* Import libraries */
import { isExpiredSessionError } from '@/lib/app/debugRequests';
import { ChartConfigDetector, type ChartConfigType } from '@/lib/conversion/ChartConfigDetector';
import { ChartJsToPlotlyConverter, type ChartJsConfiguration } from '@/lib/conversion/ChartJsToPlotlyConverter';

/* Import types */
import type { StoredPlotlyResponse } from '@/types/eeCheck';

const CHART_DEFINITION_GROUPS = [
    {
        label: 'ChartDefinition',
        definitions: [
            'nt_Z26_base_load_map_3y',
            'nt_cooling_demand_map_3y',
            'nt_heat_demand_map_3y',
            'nt_heat_price_correlation_current',
            'nt_heat_renewable_correlation_90d',
            'nt_school_electricity_map_3y',
        ],
    },
    {
        label: 'MonthlyIntervallBarChart',
        definitions: [
            'nt_production_consumption_monthly_bars_1y',
        ],
    },
    {
        label: 'PerformanceCurve',
        definitions: [
            'nt_heat_demand_correlation',
            'pv1_performance_curve',
            'wp1_cop_curve',
            'wp2_cop_curve',
            'wp3_cop_curve',
            'wp4_cop_curve',
        ],
    },
] as const;

const chartJsToPlotlyConverter = new ChartJsToPlotlyConverter();

type LoadedChartConfigType = Exclude<ChartConfigType, 'unknown'> | null;

function ChartDefinitionsTab() {
    const { t } = useI18n();
    const { accessToken, isSessionExpired, onSessionExpired } = useSession();
    const [definitionName, setDefinitionName] = useState('nt_heat_demand_map_3y');
    const [figure, setFigure] = useState<StoredPlotlyResponse | null>(null);
    const [loadedChartConfigType, setLoadedChartConfigType] = useState<LoadedChartConfigType>(null);
    const [isRequesting, setIsRequesting] = useState(false);
    const [modalState, setModalState] = useState({
        isOpen: false,
        message: '',
        title: '',
    });

    function showModal(title: string, message: string) {
        setModalState({ isOpen: true, message, title });
    }

    async function handleRequest() {
        if (!accessToken || isSessionExpired) {
            return;
        }

        setIsRequesting(true);

        try {
            const { data } = await eeCheckApi.requestStoredChartJs(accessToken, {
                definitionName,
            });

            const chartConfigType = ChartConfigDetector.detect(data);

            if (chartConfigType === 'chartjs') {
                setFigure(chartJsToPlotlyConverter.convert(data as ChartJsConfiguration));
                setLoadedChartConfigType(chartConfigType);
                return;
            }

            if (chartConfigType === 'plotly') {
                setFigure(data as StoredPlotlyResponse);
                setLoadedChartConfigType(chartConfigType);
                return;
            }

            if (chartConfigType === 'unknown') {
                throw new Error(t('The API response does not contain a valid chart definition.'));
            }
        } catch (error) {
            if (isExpiredSessionError(error)) {
                onSessionExpired();
                return;
            }

            const message = error instanceof EeCheckApiError || error instanceof Error
                ? error.message
                : t('Unknown error.');

            showModal(t('Chart Definitions'), t('Chart definition request failed: {message}', { message }));
        } finally {
            setIsRequesting(false);
        }
    }

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Chart Definitions')}</h2>
                <p>{t('Request a stored chart definition and render it as an interactive Plotly chart.')}</p>
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="flex min-h-12 w-full flex-wrap items-center gap-3 text-left">
                    <label className="text-sm font-semibold" htmlFor="chartjs-definition-name">
                        {t('Chart Definition')}
                    </label>
                    <select
                        id="chartjs-definition-name"
                        className="select select-bordered w-auto max-w-full"
                        value={definitionName}
                        onChange={(event) => setDefinitionName(event.target.value)}
                        disabled={isRequesting}
                    >
                        {CHART_DEFINITION_GROUPS.map((group) => (
                            <optgroup key={group.label} label={group.label}>
                                {group.definitions.map((definition) => (
                                    <option key={definition} value={definition}>{definition}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>
            </section>

            <div>
                <button
                    type="button"
                    className="btn btn-primary gap-2"
                    disabled={!accessToken || isSessionExpired || isRequesting}
                    onClick={() => void handleRequest()}
                >
                    <Play className="h-4 w-4" aria-hidden="true" />
                    <span>{isRequesting ? t('Requesting chart definition...') : t('Request chart definition')}</span>
                </button>
            </div>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Loaded format')}
                        </dt>
                        <dd className="mt-1 text-sm text-base-content">
                            {loadedChartConfigType === 'chartjs'
                                ? t('Chart.js JSON')
                                : loadedChartConfigType === 'plotly'
                                    ? t('Plotly JSON')
                                    : t('Not available')}
                        </dd>
                    </div>

                    <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                        <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                            {t('Automatically converted')}
                        </dt>
                        <dd className="mt-1 text-sm text-base-content">
                            {loadedChartConfigType === null
                                ? t('Not available')
                                : loadedChartConfigType === 'chartjs'
                                    ? t('Yes')
                                    : t('No')}
                        </dd>
                    </div>
                </dl>
            </section>

            <section className="dashboard-panel dashboard-panel--chart min-h-96">
                <StaticStoredPlotlyPlot figure={figure} fillParent />
            </section>

            <StatusModal
                isOpen={modalState.isOpen}
                message={modalState.message}
                onClose={() => setModalState((currentState) => ({ ...currentState, isOpen: false }))}
                title={modalState.title}
            />
        </div>
    );
}

export default ChartDefinitionsTab;
